import { ApiError } from '@api/client'
import { useI18n } from '@i18n/I18nProvider'
import { getCurrentPosition, type Position } from '@lib/geolocation'
import { Button, ButtonLink } from '@ui/Button'
import { Icon } from '@ui/Icon'
import { Screen } from '@ui/Screen'
import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react'
import { useSearch } from 'wouter'
import { cleanName, MAX_NAME, useProposeMagicPlace } from './useProposeMagicPlace'

type LocationState = { status: 'idle' | 'locating' | 'unavailable' } | { status: 'added'; position: Position }

// Lo que falta para poder enviar, en el orden del formulario (para decirlo bajo el botón).
export const missingFields = (name: string, photo: Blob | null, location: LocationState): string[] => [
	...(cleanName(name) ? [] : ['name']),
	...(photo ? [] : ['photo']),
	...(location.status === 'added' ? [] : ['location']),
]

// A dónde vuelve: se abre desde el mapa (lo normal) o desde Más (`?desde=mas`).
export const backHref = (search: string): string => (new URLSearchParams(search).get('desde') === 'mas' ? '/mas' : '/')

// Proponer un lugar mágico (spec `magic-place-proposals`): un rincón del Camino que al caminante
// le ha parecido especial. Nombre, foto y ubicación, los tres obligatorios. La foto puede ser de
// la cámara o de la galería, porque a menudo se propone después de pasar por el sitio.
export const MagicPlacePage = () => {
	const { t } = useI18n()
	const propose = useProposeMagicPlace()
	const back = backHref(useSearch())
	const [name, setName] = useState('')
	const [photo, setPhoto] = useState<File | null>(null)
	const [preview, setPreview] = useState<string | null>(null)
	const [location, setLocation] = useState<LocationState>({ status: 'idle' })

	useEffect(() => {
		if (!photo) {
			setPreview(null)
			return
		}
		const url = URL.createObjectURL(photo)
		setPreview(url)
		return () => URL.revokeObjectURL(url)
	}, [photo])

	const onPhoto = (event: ChangeEvent<HTMLInputElement>) => setPhoto(event.target.files?.[0] ?? null)

	const locate = async () => {
		setLocation({ status: 'locating' })
		const position = await getCurrentPosition()
		setLocation(position ? { status: 'added', position } : { status: 'unavailable' })
	}

	const missing = missingFields(name, photo, location)
	const submit = (event: FormEvent) => {
		event.preventDefault()
		if (missing.length > 0 || !photo || location.status !== 'added') return
		propose.mutate({ name, photo, location: location.position })
	}

	if (propose.isSuccess)
		return (
			<Screen>
				<div role="status" className="flex flex-col items-center gap-4 py-10 text-center">
					<span className="flex size-20 items-center justify-center rounded-full bg-healthy text-white">
						<Icon name="check" className="size-10" />
					</span>
					<h1 className="text-2xl">{t('magic.thanks')}</h1>
					<p className="text-lg">{t('magic.thanksDetail')}</p>
					<ButtonLink href={back} fullWidth>
						{t('magic.back')}
					</ButtonLink>
				</div>
			</Screen>
		)

	const errorMessage =
		propose.error instanceof ApiError && propose.error.offline
			? t('common.offline')
			: propose.error
				? t('magic.error')
				: null

	return (
		<Screen title={t('magic.title')} backHref={back}>
			<p className="-mt-2 mb-6 text-lg">{t('magic.intro')}</p>
			<form onSubmit={submit} className="flex flex-col gap-6">
				<div>
					<label htmlFor="magic-name" className="mb-2 block text-lg font-semibold">
						{t('magic.name')}
					</label>
					<input
						id="magic-name"
						type="text"
						value={name}
						maxLength={MAX_NAME}
						autoComplete="off"
						placeholder={t('magic.namePlaceholder')}
						onChange={(e) => setName(e.target.value)}
						className="min-h-14 w-full rounded-2xl border-2 border-earth-300 bg-white px-4 text-lg focus:border-forest-800 focus:outline-none"
					/>
				</div>

				<div>
					{preview ? (
						<div className="flex items-center gap-4 rounded-2xl bg-white p-3">
							<img
								src={preview}
								alt={t('magic.photoSelected')}
								className="size-20 rounded-xl object-cover"
							/>
							<span className="font-semibold">{t('magic.photoSelected')}</span>
							<Button variant="ghost" className="ml-auto" onClick={() => setPhoto(null)}>
								{t('magic.removePhoto')}
							</Button>
						</div>
					) : (
						<label className="flex min-h-14 cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-forest-500 bg-white px-4 font-semibold text-forest-800">
							<Icon name="camera" />
							{t('magic.photo')}
							<input type="file" accept="image/*" className="sr-only" onChange={onPhoto} />
						</label>
					)}
				</div>

				<div>
					{location.status === 'added' ? (
						<p className="flex min-h-12 items-center gap-2 font-semibold text-healthy">
							<Icon name="pin" />
							{t('magic.locationAdded')}
						</p>
					) : (
						<Button
							variant="secondary"
							fullWidth
							onClick={locate}
							disabled={location.status === 'locating'}
						>
							<Icon name="pin" />
							{location.status === 'locating' ? t('common.loading') : t('magic.location')}
						</Button>
					)}
					{location.status === 'unavailable' && (
						<p className="mt-2 text-earth-800">{t('magic.locationUnavailable')}</p>
					)}
				</div>

				<p className="text-sm text-earth-800">{t('magic.privacy')}</p>

				{errorMessage && (
					<p role="alert" className="rounded-2xl bg-damaged/10 p-4 font-semibold text-damaged">
						{errorMessage}
					</p>
				)}

				<Button type="submit" fullWidth disabled={missing.length > 0 || propose.isPending}>
					{propose.isPending ? t('magic.sending') : t('magic.submit')}
				</Button>
				{missing.length > 0 && <p className="-mt-3 text-center text-sm text-earth-800">{t('magic.missing')}</p>}
			</form>
		</Screen>
	)
}
